import { useState, useCallback, useEffect, useRef } from "react";
import { bankDetailService } from "../services";
import toast from "react-hot-toast";

export const useBankDetails = (params = {}, user = null) => {
  // State management
  const [bankDetails, setBankDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingBankDetail, setEditingBankDetail] = useState(null);

  // Loading states
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  // Throttling and retry logic
  const lastRequestTimeRef = useRef(0);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  const THROTTLE_DELAY = 1000; // 1 second

  // Fetch bank details with throttling and retry logic
  const fetchBankDetails = useCallback(async () => {
    const now = Date.now();
    if (now - lastRequestTimeRef.current < THROTTLE_DELAY) {
      console.log("Throttling request");
      return;
    }
    lastRequestTimeRef.current = now;

    setIsLoading(true);
    try {
      // Build API parameters based on user role
      const apiParams = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...params,
      };

      // For Company Owner, only fetch bank details from their company
      if (user?.role?.id === 2 && user?.company?.id) {
        apiParams.companyId = user.company.id;
      }

      const response = await bankDetailService.getAll(apiParams);
      console.log("response", response);
      setBankDetails(response.bankDetails || response.data?.bankDetails || []);
      setTotalPages(response.data?.pagination?.pages || 1);
      setTotalItems(
        response.data?.pagination?.total ||
          response.data?.total ||
          (response.bankDetails || response.data?.bankDetails || []).length
      );
      retryCountRef.current = 0; // Reset retry count on success
    } catch (error) {
      // Handle rate limiting with retry
      if (error.response?.status === 429) {
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current++;
          const retryDelay = Math.pow(2, retryCountRef.current) * 1000; // Exponential backoff
          toast.error(
            `Rate limited. Retrying in ${retryDelay / 1000} seconds...`
          );

          setTimeout(() => {
            fetchBankDetails();
          }, retryDelay);
          return;
        } else {
          toast.error("Too many requests. Please try again later.");
        }
      } else {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to fetch bank details"
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm, user, params]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== undefined) {
        setCurrentPage(1);
        fetchBankDetails();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchBankDetails]);

  // Initial fetch
  useEffect(() => {
    fetchBankDetails();
  }, [currentPage]);

  // Create bank detail
  const createBankDetail = useCallback(
    async (bankDetailData) => {
      setIsCreating(true);
      try {
        // Set isActive to true by default for new bank details
        const dataWithDefaults = { ...bankDetailData, isActive: true };
        await bankDetailService.create(dataWithDefaults);
        toast.success("Bank detail created successfully!");
        setShowModal(false);
        setEditingBankDetail(null);
        await fetchBankDetails();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to create bank detail"
        );
      } finally {
        setIsCreating(false);
      }
    },
    [fetchBankDetails]
  );

  // Update bank detail
  const updateBankDetail = useCallback(
    async (id, bankDetailData) => {
      setIsUpdating(true);
      try {
        await bankDetailService.update(id, bankDetailData);
        toast.success("Bank detail updated successfully!");
        setShowModal(false);
        setEditingBankDetail(null);
        await fetchBankDetails();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update bank detail"
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchBankDetails]
  );

  // Delete bank detail
  const deleteBankDetail = useCallback(
    async (id) => {
      setIsDeleting(true);
      try {
        await bankDetailService.delete(id);
        toast.success("Bank detail deleted successfully!");
        await fetchBankDetails();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to delete bank detail"
        );
      } finally {
        setIsDeleting(false);
      }
    },
    [fetchBankDetails]
  );

  // Toggle status
  const toggleStatus = useCallback(
    async (id) => {
      setIsToggling(true);
      try {
        // Since there's no toggleStatus endpoint, we'll need to get the current status and update it
        const bankDetail = bankDetails.find((bd) => bd.id === id);
        if (bankDetail) {
          await bankDetailService.update(id, {
            ...bankDetail,
            isActive: !bankDetail.isActive,
          });
          toast.success("Bank detail status updated successfully!");
          await fetchBankDetails();
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update bank detail status"
        );
      } finally {
        setIsToggling(false);
      }
    },
    [bankDetails, fetchBankDetails]
  );

  // Handlers
  const handleEdit = useCallback((bankDetail) => {
    setEditingBankDetail(bankDetail);
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(
    (bankDetail) => {
      if (window.confirm("Are you sure you want to delete this bank detail?")) {
        deleteBankDetail(bankDetail.id);
      }
    },
    [deleteBankDetail]
  );

  const handleToggleStatus = useCallback(
    (bankDetail) => {
      toggleStatus(bankDetail.id);
    },
    [toggleStatus]
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingBankDetail(null);
  }, []);

  const handleSubmit = useCallback(
    async (data) => {
      console.log("useBankDetails - handleSubmit called with data:", data);
      console.log("useBankDetails - data.id:", data.id);
      console.log("useBankDetails - editingBankDetail:", editingBankDetail);

      if (data.id) {
        console.log(
          "useBankDetails - Calling updateBankDetail with ID:",
          data.id
        );
        await updateBankDetail(data.id, data);
      } else {
        console.log("useBankDetails - Calling createBankDetail");
        await createBankDetail(data);
      }
    },
    [createBankDetail, updateBankDetail, editingBankDetail]
  );

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setCurrentPage(1);
  }, []);

  const handleRetry = useCallback(() => {
    retryCountRef.current = 0;
    fetchBankDetails();
  }, [fetchBankDetails]);

  const refreshBankDetails = useCallback(() => {
    fetchBankDetails();
  }, [fetchBankDetails]);

  return {
    // Data
    bankDetails,
    isLoading,
    totalPages,
    totalItems,

    // Loading states
    isCreating,
    isUpdating,
    isDeleting,
    isToggling,

    // Modal state
    showModal,
    editingBankDetail,

    // Handlers
    handleSubmit,
    handleEdit,
    handleDelete,
    handleToggleStatus,
    handleCloseModal,
    handlePageChange,
    handleClearFilters,
    handleRetry,

    // Direct functions
    createBankDetail,
    updateBankDetail,
    deleteBankDetail,
    toggleStatus,
    fetchBankDetails,
    refreshBankDetails,

    // Search
    searchTerm,
    setSearchTerm,
  };
};

export default useBankDetails;
